import React, {useMemo, useState} from 'react'
import {zodResolver} from '@hookform/resolvers/zod'
import PropTypes from 'prop-types'
import {useForm} from 'react-hook-form'

import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Flex,
    Heading,
    Image,
    Input,
    Text,
    useMediaQuery
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'

import {useNewsletterSubscribeMutation} from '../../clients/ssr/hooks/use-newsletter-subscribe'
import {colors, localStorageKeys} from '../../constants'
import {newsletterSchema} from '../../validations'
import {contentfulMediaFormatter} from '../../utils'

const NewsLetter = ({data}) => {
    const {formatMessage} = useIntl()
    const [isDesktop] = useMediaQuery('(min-width: 992px)')

    const {
        title,
        subtitle = '',
        media = null,
        theme = null,
        action = null
    } = data

    const textColor = theme?.color || colors.black
    const image = useMemo(() => {
        if (!media) return
        const assets = contentfulMediaFormatter(media)
        return !isDesktop && assets?.mobile?.url
            ? assets?.mobile
            : assets?.desktop
    }, [media, isDesktop])

    const form = useForm({resolver: zodResolver(newsletterSchema)})
    const [formSubmitSuccess, setFormSubmitSuccess] = useState(false)
    const newsletterMutation = useNewsletterSubscribeMutation()

    const sendNewsletterSubscriptionRequest = async (payload) => {
        newsletterMutation.mutate(payload, {
            onSuccess: () => {
                localStorage.setItem(
                    localStorageKeys.newsLetterSubscription,
                    'true'
                )
                setFormSubmitSuccess(true)
                if (window) {
                    window.dataLayer = window.dataLayer || []
                    window.dataLayer.push({
                        event: 'newsletter',
                        newsletterEmail: payload.email
                    })
                }
            },
            onError: (error) => {
                form.setError('global', {
                    type: 'manual',
                    message: error?.message
                })
            }
        })
    }

    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            px={{base: '24px', xl: '40px'}}
            pb={{base: '24px', md: '40px'}}
            my="40px"
        >
            <Flex
                as="form"
                p={{base: '24px', lg: '12px'}}
                gap={{base: '24px', xl: '100px'}}
                width="100%"
                borderRadius="32px"
                alignItems="center"
                flexFlow={{base: 'column', xl: 'row'}}
                backgroundColor={
                    theme?.backgroundColor || colors.footerBgGradient
                }
                onSubmit={form.handleSubmit(sendNewsletterSubscriptionRequest)}
            >
                {image && (
                    <Box width={{base: '100%', lg: '40%'}}>
                        <Image
                            src={image?.url}
                            alt={`Newsletter image - ${title}`}
                            objectFit="contain"
                            style={{
                                objectFit: 'contain'
                            }}
                        />
                    </Box>
                )}
                <Flex flexDirection="column" gap="24px">
                    <Flex gap="12px" width="100%" flexDirection="column">
                        <Heading
                            fontSize={{base: '24px', md: '24px'}}
                            lineHeight={{base: '28.18px', md: '37.50px'}}
                            fontFamily="Raleway"
                            fontWeight="500"
                            color={textColor}
                        >
                            {title}
                        </Heading>
                        {subtitle && (
                            <Text
                                fontWeight="400"
                                fontSize="16px"
                                fontFamily="Raleway"
                                lineHeight="24px"
                                color={textColor}
                            >
                                {subtitle}
                            </Text>
                        )}
                    </Flex>
                    <Flex flexDirection="column" gap="12px">
                        {formSubmitSuccess ? (
                            <Flex
                                flexDirection="column"
                                gap={{base: '14px', md: '29px'}}
                                justifyContent="center"
                            >
                                <Heading
                                    variant="h3"
                                    fontFamily="Roboto"
                                    fontWeight="300"
                                    textAlign="center"
                                >
                                    {formatMessage({
                                        id: 'rennai.newsletter_subscription',
                                        defaultMessage:
                                            'Thank you for subscribing!'
                                    })}
                                </Heading>
                            </Flex>
                        ) : (
                            <>
                                {form?.formState?.errors?.global && (
                                    <Alert status="error">
                                        <AlertIcon
                                            color="red.500"
                                            boxSize={4}
                                        />
                                        <Text fontSize="sm" ml={3}>
                                            {
                                                form?.formState?.errors?.global
                                                    ?.message
                                            }
                                        </Text>
                                    </Alert>
                                )}
                                <Flex flexFlow="column">
                                    <Flex
                                        p="1px"
                                        width={{base: '100%', xl: '70%'}}
                                        alignItems="center"
                                        borderRadius="34px"
                                        justifyContent="end"
                                        border={`1px solid ${colors.quilGray}`}
                                    >
                                        <Input
                                            type="email"
                                            {...form.register('email')}
                                            placeholder={formatMessage({
                                                defaultMessage: 'Email address',
                                                id: 'email_input'
                                            })}
                                            height="32px"
                                            fontSize="16px"
                                            fontWeight="400"
                                            lineHeight="24px"
                                            fontFamily="Raleway"
                                            color={textColor}
                                            _placeholder={{
                                                display: 'flex',
                                                height: '22px',
                                                alignItems: 'center'
                                            }}
                                            bg="transparent"
                                            border="none"
                                            boxShadow="none"
                                        />
                                        <Button
                                            type="submit"
                                            sx={{
                                                borderRadius: '32px',
                                                fontWeight: 500,
                                                fontSize: '14px',
                                                color: textColor,
                                                maxHeight: '32px',
                                                position: 'absolute',
                                                fontFamily: 'Raleway',
                                                backgroundColor:
                                                    colors.quilGray,
                                                _hover: {
                                                    backgroundColor:
                                                        colors.platinum
                                                }
                                            }}
                                            isLoading={
                                                newsletterMutation.isLoading
                                            }
                                        >
                                            {action ||
                                                formatMessage({
                                                    defaultMessage: 'Subscribe',
                                                    id: 'rennai.form.button.subscribe'
                                                })}
                                        </Button>
                                    </Flex>
                                    {form?.formState?.errors?.email && (
                                        <Text
                                            fontSize="14px"
                                            color="red.500"
                                            marginTop="6px"
                                        >
                                            {
                                                form?.formState?.errors?.email
                                                    ?.message
                                            }
                                        </Text>
                                    )}
                                </Flex>
                            </>
                        )}
                    </Flex>
                </Flex>
            </Flex>
        </Box>
    )
}

NewsLetter.displayName = 'NewsLetter'

NewsLetter.propTypes = {
    data: PropTypes.shape({
        title: PropTypes.string,
        subtitle: PropTypes.string,
        action: PropTypes.string,
        theme: PropTypes.shape({
            backgroundColor: PropTypes.string,
            color: PropTypes.string
        }),
        media: PropTypes.shape({
            desktop: PropTypes.shape({
                url: PropTypes.string
            })
        })
    })
}

export default NewsLetter
